import * as React from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, Badge } from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import type { ItemType } from 'antd/lib/menu/hooks/useItems';
import { InventoryClient } from '../utils/scanner-backend';
import { ApiConfig } from '../utils/AuthorizedApiBase';

const inventoryClient = new InventoryClient(new ApiConfig());

interface NavMenuProps {
    onOpenCharacterMapSetup?: () => void;
}

interface NavMenuState {
    temporaryPositionsCount: number;
}

export default class NavMenu extends React.Component<NavMenuProps, NavMenuState> {
    private intervalId?: NodeJS.Timeout;

    constructor(props: NavMenuProps) {
        super(props);
        this.state = {
            temporaryPositionsCount: 0
        };
    }

    componentDidMount() {
        this.loadTemporaryPositionsCount();
        // Refresh count every 30 seconds
        this.intervalId = setInterval(() => {
            this.loadTemporaryPositionsCount();
        }, 30000);
    }

    componentWillUnmount() {
        if (this.intervalId) {
            clearInterval(this.intervalId);
        }
    }

    private async loadTemporaryPositionsCount() {
        try {
            const positions = await inventoryClient.getTemporaryPositions();
            this.setState({ temporaryPositionsCount: positions.length });
        } catch (error) {
            console.error('Failed to load temporary positions count:', error);
        }
    }

    private getMenuItems(): ItemType[] {
        const { temporaryPositionsCount } = this.state;

        return [
            {
                key: 1,
                label: <NavLink className="text-dark" to="/incoming">Príjem tovaru</NavLink>
            },
            {
                key: 2,
                label: <a className="text-dark" href="https://backflip.signexpert.sk/">Výdaj tovaru</a>
            },
            {
                key: 3,
                label: <NavLink className="text-dark" to="/inventory-move">Presun tovaru</NavLink>,
            },
            {
                key: 4,
                label: <NavLink className="text-dark" to="/position-stocktaking">Inventúra pozície</NavLink>,
            },
            {
                key: 5,
                label: <NavLink className="text-dark" to="/sku-stocktaking">Inventúra SKU</NavLink>,
            },
            {
                key: 6,
                label: (
                    <NavLink to="/temporary-positions" style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
                        <Badge
                            count={temporaryPositionsCount}
                            offset={[10, 0]}
                            style={{
                                backgroundColor: temporaryPositionsCount > 0 ? '#ff4d4f' : '#52c41a',
                                fontSize: '12px',
                                fontWeight: 'bold'
                            }}
                        >
                            <span style={{ color: 'rgba(255, 255, 255, 0.85)' }}>Dočasné pozície</span>
                        </Badge>
                    </NavLink>
                ),
            },
            {
                key: 7,
                label: <NavLink className="text-dark" to="/product-search">Tlač štítkov</NavLink>,
            },
            {
                key: 8,
                label: <NavLink className="text-dark" to="/recommended-moves">Odporúčané presuny</NavLink>,
            },
            {
                key: 10,
                label: (
                    <a
                        className="text-dark"
                        onClick={(e) => {
                            e.preventDefault();
                            this.props.onOpenCharacterMapSetup?.();
                        }}
                        style={{ cursor: 'pointer' }}
                    >
                        <SettingOutlined /> Mapovanie znakov
                    </a>
                ),
            }
        ];
    }

    public render() {
        return <NavMenuWithRouter getMenuItems={() => this.getMenuItems()} />;
    }
}

function NavMenuWithRouter({ getMenuItems }: { getMenuItems: () => ItemType[] }) {
    const location = useLocation();
    const menuItems = getMenuItems();

    // Find the menu item whose route matches the current pathname
    const selectedItem = menuItems.find(item => {
        if (!item || typeof item !== 'object' || !('label' in item)) return false;
        const label = item.label as React.ReactElement;
        const to = label?.props?.to as string;
        if (to === "/incoming") {
            return location.pathname === "/incoming" || location.pathname.startsWith("/order-intake");
        }
        return to && location.pathname.startsWith(to);
    });

    const selectedKey = selectedItem?.key?.toString() || '';

    return (
        <Menu
            theme="dark"
            mode="horizontal"
            items={menuItems}
            selectedKeys={selectedKey ? [selectedKey] : []}
        />
    );
}
